import { Box, CollapsibleCard, IconButton } from '@atgfr8/atg-ui-tailwind';
import { AutoComplete, RadioGroup } from '@components';
import { ltlCustomerSetting } from '@store/customers/customerDetails/LTL';
import { ILtlCarrierSelectionItem } from '@store/customers/customerDetails/LTL/types';
import { DeleteIcon, AccountIcon } from '@atgfr8/icons';
import { CarrierInfo } from '@typeDefinitions/carrierTypes';
import { size } from 'lodash';
import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  For,
  Setter,
  Show,
} from 'solid-js';

import { InitialLTLFormValues } from '../Ltl';
import { blockedCarriers as classes } from './classes';

const STRATEGY_BLOCK = 1;
const STRATEGY_INCLUDE = 2;

const CarrierSelection = (props: {
  setFields: (key: string, value: unknown) => void;
  data: Accessor<InitialLTLFormValues>;
  setIsDirty: Setter<boolean>;
  setIsCarriersDirty: Setter<boolean>;
  onRequestStrategyToggle: (next: number) => void;
  onSectionChange: () => void;
}) => {
  const [localCarrierDetails, setLocalCarrierDetails] = createSignal<
    ILtlCarrierSelectionItem[]
  >(ltlCustomerSetting.data.carriers);

  createEffect(() => {
    const customerId = ltlCustomerSetting.data.customerId;
    const storeCarriers = ltlCustomerSetting.data.carriers;
    setLocalCarrierDetails((prev) => {
      const storeIds = new Set(storeCarriers.map((c) => c.carrierId));
      const localOnly = prev.filter(
        (c) => c.customerId === customerId && !storeIds.has(c.carrierId),
      );
      return [...storeCarriers, ...localOnly];
    });
  });

  const strategyType = () =>
    props.data().carrierSelectionStrategyType ?? STRATEGY_BLOCK;

  const strategyLabel = () =>
    strategyType() === STRATEGY_BLOCK ? 'Block' : 'Include';

  const displayedCarriers = createMemo(() => {
    const ids = new Set(props.data().carrierIds);
    return localCarrierDetails().filter((c) => ids.has(c.carrierId));
  });

  createEffect((previousCount: number) => {
    const currentCount = displayedCarriers().length;

    if (
      previousCount > 0 &&
      currentCount === 0 &&
      props.data().carrierSelectionStrategyType !== STRATEGY_BLOCK
    ) {
      props.onRequestStrategyToggle(STRATEGY_BLOCK);
    }

    return currentCount;
  }, displayedCarriers().length);

  const isCarrierAlreadyAdded = (carrier: CarrierInfo) =>
    props.data().carrierIds.includes(carrier.id);

  const onItemSelect = (item: CarrierInfo) => {
    if (isCarrierAlreadyAdded(item)) return;

    props.setFields('carrierIds', [...props.data().carrierIds, item.id]);
    props.setIsDirty(true);
    props.setIsCarriersDirty(true);
    props.onSectionChange();

    if (!localCarrierDetails().some((c) => c.carrierId === item.id)) {
      setLocalCarrierDetails((prev) => [
        ...prev,
        {
          customerId: ltlCustomerSetting.data.customerId,
          carrierId: item.id,
          carrierName: item.name,
          carrierDOT: item.dOT,
          carrierMC: item.mC,
          carrierSelectionType: strategyType(),
          carrierSelectionTypeDescription:
            strategyType() === STRATEGY_INCLUDE ? 'Include' : 'Block',
        } as ILtlCarrierSelectionItem,
      ]);
    }
  };

  const onDelete = (carrierId: number) => {
    props.setFields(
      'carrierIds',
      props.data().carrierIds.filter((id) => id !== carrierId),
    );
    props.setIsDirty(true);
    props.setIsCarriersDirty(true);
    props.onSectionChange();
  };

  const onClearAll = () => {
    props.setFields('carrierIds', []);
    props.setIsDirty(true);
    props.setIsCarriersDirty(true);
    props.onSectionChange();
  };

  return (
    <CollapsibleCard
      startTitle="Carrier Selection"
      loading={ltlCustomerSetting.isLoading}
    >
      <Box class={classes.blockedContainer}>
        Control which carriers customers can see quotes from. Use{' '}
        <strong>Block</strong> to exclude specific carriers or{' '}
        <strong>Include</strong> to allow only specific carriers.
      </Box>

      <Box class="flex items-center gap-2 mb-3">
        <Box class="text-sm font-medium text-[#000000]">Mode:</Box>
        <RadioGroup<number>
          value={props.data().carrierSelectionStrategyType ?? undefined}
          onChange={props.onRequestStrategyToggle}
          options={[
            { label: 'Block', value: STRATEGY_BLOCK },
            { label: 'Include', value: STRATEGY_INCLUDE },
          ]}
        />
      </Box>

      <Show when={props.data().carrierSelectionStrategyType != null}>
        <Box class="flex items-center justify-between mb-1">
          <Box />
          <Show when={size(displayedCarriers()) > 0}>
            <button
              type="button"
              class="text-xs text-[#026EA1] underline cursor-pointer bg-transparent border-none p-0"
              onClick={onClearAll}
            >
              Clear list
            </button>
          </Show>
        </Box>
        <AutoComplete
          id="carrier-selection-autocomplete"
          endpoint="ltl/autocomplete/?term="
          onItemSelect={(item) => onItemSelect(item as CarrierInfo)}
          clearSearch={true}
          multipleValueRender={() => (
            <For each={displayedCarriers()}>
              {(item) => (
                <Box class="flex mt-1 justify-between p-2 border-2 shadow-xs">
                  <Box class="flex gap-8 items-center">
                    <AccountIcon class="text-[#026EA1]" />
                    <Box class="font-medium text-base">
                      {item.carrierName}
                      <Box class={classes.carrierMcDotText}>
                        MC:{' '}
                        <span class="text-sm! font-normal">
                          {item.carrierMC}
                        </span>{' '}
                        | DOT:{' '}
                        <span class="text-sm! font-normal">
                          {item.carrierDOT}
                        </span>
                      </Box>
                    </Box>
                  </Box>
                  <Box class="flex flex-col items-center justify-center w-14">
                    <IconButton
                      onClick={() => onDelete(item.carrierId)}
                      dataTestId="carrier-selection-delete-icon-button"
                    >
                      <DeleteIcon class="text-[22px]! text-red-700" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </For>
          )}
          renderMultipleKey="carrierName"
          mode="multiple"
          defaultValue=""
          label=""
          placeholder={`Add carriers to ${strategyLabel()} list...`}
          version="v3"
          renderItem={(item) => {
            const item2 = item as CarrierInfo;
            return (
              <Box class="flex gap-1">
                <AccountIcon class="text-[#026EA1]" />
                <Box class="font-medium text-base">
                  {item2.name}
                  <Box class={classes.carrierMcDotText}>
                    MC: <span class="text-sm! font-normal">{item2.mC}</span> |
                    DOT: <span class="text-sm! font-normal">{item2.dOT}</span>
                  </Box>
                </Box>
              </Box>
            );
          }}
        />
        <Show when={size(displayedCarriers()) === 0}>
          <Box class={classes.noCarrierCotainer}>
            <Box class={classes.noCarrierText}>
              {strategyLabel() === 'Block'
                ? 'No carriers are blocked. Customers will be able to see quotes from all carriers.'
                : 'No carriers are included. Customers will not see quotes from any carriers.'}
            </Box>
          </Box>
        </Show>
      </Show>

      <Show when={props.data().carrierSelectionStrategyType == null}>
        <Box class={classes.noCarrierCotainer}>
          <Box class={classes.noCarrierText}>
            Select a mode above to manage carriers.
          </Box>
        </Box>
      </Show>
    </CollapsibleCard>
  );
};

export default CarrierSelection;
